import React, { useCallback, useEffect, useState } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  getCrmSettings,
  updateCrmSettings,
} from "../services/crmSettingsService";
import useCrmPermissions from "../hooks/useCrmPermissions";
import "../styles/crm.css";

const CRMSettingsPage = () => {
  const { companyId } = useCompany();
  const { isAdmin } = useCrmPermissions();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const settingsRow = await getCrmSettings(companyId);
      setSettings(settingsRow);
    } catch (error) {
      setMessage(error.message || "Failed to load CRM settings");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSettingsChange = useCallback((field, value) => {
    setSettings((previous) => ({
      ...previous,
      [field]: value,
    }));
  }, []);

  const handleSaveSettings = useCallback(async () => {
    if (!companyId || !settings || !isAdmin) {
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      await updateCrmSettings(companyId, {
        stale_opportunity_days: Number(settings.stale_opportunity_days || 14),
        collections_followup_days: Number(
          settings.collections_followup_days || 3
        ),
        auto_create_collection_cases: Boolean(
          settings.auto_create_collection_cases
        ),
        default_quote_terms: settings.default_quote_terms || null,
        default_quote_notes: settings.default_quote_notes || null,
      });

      await load();
      setMessage("CRM settings updated successfully.");
    } catch (error) {
      setMessage(error.message || "Failed to update CRM settings");
    } finally {
      setSaving(false);
    }
  }, [companyId, settings, isAdmin, load]);

  if (loading || !settings) {
    return (
      <div className="crm-message">{message || "Loading CRM settings..."}</div>
    );
  }

  if (!isAdmin) {
    return (
      <div>
        <h2 className="crm-page-title">CRM Settings</h2>
        <div className="crm-message">Only admins can manage CRM settings.</div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="crm-page-title">CRM Settings</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card">
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          General Settings
        </h3>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Stale Opportunity Days</label>
            <input
              className="crm-input"
              type="number"
              value={settings.stale_opportunity_days || 14}
              onChange={(event) =>
                handleSettingsChange(
                  "stale_opportunity_days",
                  event.target.value
                )
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Collections Follow-up Days</label>
            <input
              className="crm-input"
              type="number"
              value={settings.collections_followup_days || 3}
              onChange={(event) =>
                handleSettingsChange(
                  "collections_followup_days",
                  event.target.value
                )
              }
            />
          </div>
        </div>

        <div className="crm-form-group">
          <label>Default Quote Terms</label>
          <input
            className="crm-input"
            value={settings.default_quote_terms || ""}
            onChange={(event) =>
              handleSettingsChange("default_quote_terms", event.target.value)
            }
          />
        </div>

        <div className="crm-form-group">
          <label>Default Quote Notes</label>
          <textarea
            className="crm-textarea"
            rows={4}
            value={settings.default_quote_notes || ""}
            onChange={(event) =>
              handleSettingsChange("default_quote_notes", event.target.value)
            }
          />
        </div>

        <div className="crm-inline-actions" style={{ marginBottom: "16px" }}>
          <label className="crm-checkbox-row">
            <input
              type="checkbox"
              checked={Boolean(settings.auto_create_collection_cases)}
              onChange={(event) =>
                handleSettingsChange(
                  "auto_create_collection_cases",
                  event.target.checked
                )
              }
            />
            <span>Auto-create collection cases for overdue invoices</span>
          </label>
        </div>

        <button
          className="crm-primary-btn"
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default CRMSettingsPage;
